'use client';

import { HeadingValue, ICmsImage } from '@bayada/interfaces';
import { CmsImage, Heading } from '@bayada/shared/ui-components';
import {
  WrapperContainer,
  getWrapperProps
} from '@bayada/shared/ui-components';
import { CmsRichText } from '../cms-rich-text/cms-rich-text';
import { MarkDown } from '@bayada/shared/ui-components';
import React, { useState } from 'react';
import styles from './content-showcase.module.scss';

export interface ContentShowcaseProps {
  internalName: string;
  heading: string;
  headingValue: HeadingValue; //check
  richText: Document; //check
  backgroundStyle: string;
  backgroundImage: ICmsImage | null;
  showcaseImages: ICmsImage[];
}

export const ContentShowcase = (props: ContentShowcaseProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const total = props.showcaseImages.length;
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + total) % total);
  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % total);

  const getIndex = (offset: number) => (currentIndex + offset + total) % total;

  const backgroundImage = props?.backgroundImage?.imagelarge
    ? props.backgroundImage.imagelarge.startsWith('//')
      ? `https:${props.backgroundImage.imagelarge}`
      : props.backgroundImage.imagelarge
    : '';

  const backgroundImageUrl = backgroundImage ? `url("${backgroundImage}")` : '';
  const hasBackgroundClass = backgroundImage ? styles.showcaseBackground : '';
  const { wrapperContainer } = getWrapperProps(props);

  return (
    <WrapperContainer
      wrapper={wrapperContainer}
      className={`content-section-container`}
      containerCoverClass="py-10 sm:py-12 md:py-14"
    >
      {props.heading && (
        <Heading
          className={`font-frutiger font-bold`}
          type={props.headingValue}
          aria-label="top-heading"
        >
          <span dangerouslySetInnerHTML={{ __html: MarkDown(props.heading) }} />
        </Heading>
      )}

      {props.richText && (
        <CmsRichText
          value={props.richText}
          className={`font-normal t-17 my-4`}
        />
      )}

      {/* Background Image Showcase */}

      <div
        className={`${hasBackgroundClass} ${styles.showcaseClass} relative bg-cover bg-center flex items-center justify-center`}
        style={
          { '--background-image': backgroundImageUrl } as React.CSSProperties
        }
      >
        {/* Left Gradient Overlay */}
        <div className={`${styles.leftGradient} ${styles.gradient}`} />

        {/* Right Gradient Overlay */}
        <div className={`${styles.rightGradient} ${styles.gradient}`} />

        {/* Image Showcase */}
        <div
          className="flex items-center relative z-5"
          aria-label="Image Showcase"
        >
          {[
            getIndex(-2),
            getIndex(-1),
            currentIndex,
            getIndex(1),
            getIndex(2)
          ].map((index, i) => {
            const isCenter = i === 2;
            const wrapperClass = `${styles.imageWrapper} ${isCenter ? styles.center : styles.side} `;

            return (
              <div key={index} className={wrapperClass}>
                <CmsImage
                  {...props.showcaseImages[index]}
                  className={`z-5 w-full h-full object-cover ${styles.showcaseImage}}`}
                  style={{
                    transform: isCenter ? 'scale(1.1)' : 'scale(0.9)',
                    transition: 'transform 0.3s ease-in-out'
                  }}
                  alt={props.showcaseImages[index].altText || `Slide ${index}`}
                  // fill= {true}
                />
              </div>
            );
          })}
        </div>

        {/* Indicators */}
        <div
          className="z-20 absolute bottom-4 transform flex justify-center gap-2 w-3/4"
          aria-label="Showcase Controls"
        >
          <button
            onClick={prevSlide}
            className={`${styles.arrows} text-white leading-normal`}
            aria-label="Left Arrow"
          >
            &larr;
          </button>

          {props.showcaseImages.map((img, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setCurrentIndex(index)}
              className={`${styles.indicators} ${
                index === currentIndex ? 'bg-white' : 'bg-white/50'
              }`}
              aria-label={
                img.ariaLabel
                  ? `Go to image ${img.ariaLabel}`
                  : img.altText
                    ? `Go to image ${img.altText}`
                    : `Go to image ${index + 1}`
              }
            />
          ))}

          <button
            onClick={nextSlide}
            className={`${styles.arrows} text-white leading-normal`}
            aria-label="Right Arrow"
          >
            &rarr;
          </button>
        </div>
      </div>
    </WrapperContainer>
  );
};
